import { useState } from "react";
import { Box, Flex, FormDialog, MenuItem, Select, TextField, Typography } from "flux";
import { BaseDialogProps } from "@adora/shared/contexts/dialog-context";
import { BrandStatus } from "@adora/shared/types/brand";

export interface BaseBrandDialogProps<T> extends BaseDialogProps {
  initialValues?: T;
  actionButtonText: string;
  performAction: (values: { name: string; description: string; s3Bucket: string }) => Promise<void>;
}

interface BrandFormValues {
  name: string;
  description: string;
  s3Bucket: string;
  status: BrandStatus;
}

const defaultValues: BrandFormValues = {
  name: "",
  description: "",
  s3Bucket: "",
  status: "active"
};

const BrandStatusOptions: Array<{ value: BrandStatus; label: string; description: string }> = [
  { value: "active", label: "Active", description: "Full paid subscriptions" },
  { value: "locked", label: "Locked", description: "Churned or non-active former paid subscriptions" },
  { value: "trial", label: "Trial", description: "Free trial temporary subscriptions" },
  { value: "expired", label: "Expired", description: "Expired trial subscriptions" }
];

const BaseBrandDialog: React.FC<BaseBrandDialogProps<BrandFormValues>> = ({
  open,
  onClose,
  initialValues = defaultValues,
  actionButtonText,
  performAction
}) => {
  const [values, setValues] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const hasName = Boolean(values.name);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    setSubmitting(true);
    event.preventDefault();

    await performAction(values);

    setSubmitting(false);
    onClose(true);
  };

  return (
    <FormDialog
      title={hasName ? "Edit Brand" : "Create Brand"}
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      primaryButtonText={actionButtonText}
      isPrimaryButtonDisabled={submitting}
      dataDogIdentifier="create or edit brand"
    >
      <Flex flexDirection="column" gap="8px">
        <Box>
          <Typography variant="body1">Brand Name</Typography>
          <TextField
            type="text"
            name="name"
            value={values.name}
            onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Brand Name"
            required={hasName ? false : true}
            fullWidth
            dataDogIdentifier="brand name"
          />
        </Box>
        <Box>
          <Typography variant="body1">Description</Typography>
          <TextField
            type="text"
            name="description"
            value={values.description}
            onChange={(e) => setValues((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Description"
            fullWidth
            dataDogIdentifier="brand description"
          />
        </Box>
        <Box>
          <Typography variant="body1">S3 Bucket</Typography>
          <TextField
            type="text"
            name="s3Bucket"
            value={values.s3Bucket}
            onChange={(e) => setValues((prev) => ({ ...prev, s3Bucket: e.target.value }))}
            placeholder="S3 Bucket"
            fullWidth
            dataDogIdentifier="brand s3 bucket"
          />
        </Box>
        <Box>
          <Typography variant="body1">Brand Status</Typography>
          <Select
            value={values.status}
            onChange={(e) => setValues((prev) => ({ ...prev, status: e.target.value as BrandStatus }))}
            fullWidth={true}
          >
            {BrandStatusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value} size="medium">
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="body1">{option.label}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {option.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Flex>
    </FormDialog>
  );
};

export default BaseBrandDialog;
